import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ShieldCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function Start() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", organization: "", email: "", industry: "", need: "",
    operational_consent: false, marketing_consent: false, honeypot: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState("degraded");
  const formStart = useMemo(() => Date.now(), []);

  useEffect(() => {
    api.get("/entitlements/protection-mode").then(({ data }) => setMode(data.mode)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    for (const k of ["name", "organization", "email", "need"]) {
      if (!String(form[k]).trim()) return toast.error("Please complete the required fields.");
    }
    if (!form.operational_consent) return toast.error("Operational consent is required to receive emails for this project.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/entitlements/start", { ...form, form_start_ms: formStart });
      localStorage.setItem("aiude_entitlement_id", data.entitlement_id);
      toast.success("Check your email for a six-digit verification code.");
      nav(`/verify?entitlement=${encodeURIComponent(data.entitlement_id)}`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503) {
        toast.error(err?.response?.data?.detail || "We couldn't send your verification email right now. Your free demonstration has not been consumed. Please try again later.");
      } else {
        toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
      }
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <figure className="panel-elev overflow-hidden rounded-md" data-testid="start-promo-figure">
        <div className="px-4 pt-4 pb-2">
          <div className="font-tech text-[#00D2FF] tracking-widest">A.I.U.D.E. · Cinematic Promo · 59 s</div>
          <div className="text-white text-lg font-semibold mt-1">Shift the Grid</div>
        </div>
        <video
          className="w-full aspect-video bg-black"
          controls
          playsInline
          preload="metadata"
          poster="/promo/poster.jpg"
          data-testid="start-promo-video"
        >
          <source src="/promo/promo.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <figcaption className="px-4 py-3 text-xs text-slate-400">
          Watch the overview, then complete the form below to receive your six-digit verification code by email.
        </figcaption>
      </figure>

      <div>
        <div className="font-tech mb-2">Stage 00 — Contact & Consent</div>
        <h1 className="text-3xl lg:text-4xl font-semibold text-white">Begin a free demonstration project</h1>
        <p className="text-slate-400 mt-3 text-sm">
          Provide contact information so we can email your verification and delivery codes. Your
          project remains private and is never displayed in a public directory.
        </p>
      </div>

      {mode !== "active" && (
        <div className="panel-elev p-4 text-xs text-slate-400 flex items-start gap-3" data-testid="protection-mode-note">
          <ShieldCheck className="w-4 h-4 text-[#C9A961] mt-0.5" />
          <div>
            Running in degraded-protection mode: stricter rate limits, honeypot, and form timing checks are active.
            CAPTCHA is <b>not claimed as active</b>.
          </div>
        </div>
      )}

      <form onSubmit={submit} className="panel-elev p-6 lg:p-10 space-y-5" data-testid="start-form">
        <input type="text" name="aiude_ignore_this_field" value={form.honeypot} onChange={(e) => set("honeypot", e.target.value)}
          tabIndex={-1} autoComplete="off" readOnly
          style={{ position: "absolute", left: "-10000px", height: 0, width: 0, opacity: 0 }} aria-hidden="true" />

        {[
          ["name", "Name", "text"],
          ["organization", "Organization", "text"],
          ["email", "Email", "email"],
          ["industry", "Industry", "text"],
        ].map(([k, l, t]) => (
          <div key={k}>
            <label className="label-cad">{l}{["name","organization","email"].includes(k) && <span className="text-[#00D2FF]"> *</span>}</label>
            <input type={t} className="input-cad" value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`start-${k}`} autoComplete={k === "email" ? "email" : "off"} />
          </div>
        ))}
        <div>
          <label className="label-cad">General product-development need<span className="text-[#00D2FF]"> *</span></label>
          <textarea rows={4} className="input-cad resize-none" value={form.need} onChange={(e) => set("need", e.target.value)} data-testid="start-need" />
        </div>

        <div className="space-y-3 pt-2 border-t border-[#1E2F48]">
          <label className="flex items-start gap-3 text-sm text-slate-200 cursor-pointer">
            <input type="checkbox" className="mt-1 accent-[#00D2FF]" checked={form.operational_consent}
              onChange={(e) => set("operational_consent", e.target.checked)} data-testid="start-op-consent" />
            <span>I consent to receiving verification and project-delivery emails for this A.I.U.D.E. demonstration project.<span className="text-[#00D2FF]"> *</span></span>
          </label>
          <label className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
            <input type="checkbox" className="mt-1 accent-[#C9A961]" checked={form.marketing_consent}
              onChange={(e) => set("marketing_consent", e.target.checked)} data-testid="start-mkt-consent" />
            <span>I permit Talons Inclusion Factory to contact me later regarding pilots, partnerships, and services. <span className="text-slate-500">(optional)</span></span>
          </label>
        </div>

        <button type="submit" className="btn-primary w-full justify-center" disabled={submitting} data-testid="start-submit">
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <><Mail className="w-4 h-4" /> Continue — email me a verification code</>}
        </button>
      </form>
    </div>
  );
}
